<template>
  <div class="flex">
    <div class="w-full px-4">
      <div class="flex -mx-4">
        <div class="w-64 px-4">
          <template v-if="cover">
            <img
              :key="`${_uid}-campaign-cover`"
              :srcset="cover.srcsets || ''"
              @load.once="onImageLoad"
              sizes="1px"
              :src="cover.url"
              class="block rounded w-full"
            />
          </template>
          <template v-else-if="project.cover">
            <img :key="`${_uid}-project-cover`" :src="project.cover" class="block rounded w-full" />
          </template>
          <template v-else>
            <div class="aspect-ratio-16/9 w-full rounded bg-gray-200" />
          </template>
        </div>
        <div class="flex-1 px-4 space-y-4">
          <div class="justify-between flex items-start">
            <div>
              <h3 class="text-base mb-2">
                {{ campaign.name }}
              </h3>
              <BaseBadge green v-if="isActive"> ACTIVE </BaseBadge>
            </div>
            <VDropdown
              v-if="$membershipHasPermissionTo('edit contact lists,delete contact lists')"
              right
            >
              <template #button="dropdownProps">
                <button class="btn btn-ghost px-4" @click="dropdownProps.open">
                  <FontAwesomeIcon icon="ellipsis-v" fixed-width />
                </button>
              </template>

              <template #default="dropdownProps">
                <VDropdownMenu>
                  <a
                    v-if="$membershipHasPermissionTo('edit contact lists')"
                    role="button"
                    :class="[
                      'block',
                      'px-4',
                      'py-2',
                      'hover:bg-gray-200',
                      'transition-color',
                      'transition-fast',
                      'text-right',
                    ]"
                    @click="dropdownProps.close(() => $emit('campaign-edit', campaign))"
                    >Edit</a
                  >
                  <a
                    v-if="$membershipHasPermissionTo('delete contact lists')"
                    role="button"
                    :class="[
                      'block',
                      'px-4',
                      'py-2',
                      'hover:bg-gray-200',
                      'transition-color',
                      'transition-fast',
                      'text-right',
                    ]"
                    @click="dropdownProps.close(() => $emit('campaign-delete', campaign))"
                    >Remove</a
                  >
                </VDropdownMenu>
              </template>
            </VDropdown>
          </div>
          <p v-if="campaign.description" class="text-muted text-sm">
            {{ campaign.description }}
          </p>
        </div>
      </div>
      <!-- General -->

      <div class="flex -mx-4 py-3">
        <div class="flex-none px-4">
          <span class="text-xs uppercase font-semibold text-gray-600">starts</span>
          <p v-if="campaign.dateStartForHumans" v-text="campaign.dateStartForHumans" />
          <p v-else>-</p>
        </div>
        <div class="flex-none px-4">
          <span class="text-xs uppercase font-semibold text-gray-600">ends</span>
          <p v-if="campaign.dateEndForHumans" v-text="campaign.dateEndForHumans" />
          <p v-else>-</p>
        </div>
        <div class="flex-none px-4">
          <span class="text-xs uppercase font-semibold text-gray-600">budget</span>
          <p v-if="campaign.budget">{{ campaign.budget }}&euro;</p>
          <p v-else>-</p>
        </div>
        <div class="flex-none px-4">
          <span class="text-xs uppercase font-semibold text-gray-600">target</span>
          <p>
            <span v-if="campaign.result" v-text="campaign.result" />
            {{ campaign.target ? campaign.target : '-' }}
          </p>
        </div>
        <div class="flex-none px-4 text-right">
          <span class="text-xs uppercase font-semibold text-gray-600"> budget / target</span>
          <p>{{ budgetPerResult ? budgetPerResult : '-' }}&euro;</p>
        </div>
        <div class="flex-none px-4 ml-auto">
          <div
            :class="[
              'flex',
              'bg-gray-900',
              'px-6',
              'pt-1',
              'pb-3',
              '-my-3',
              'rounded',
              'text-white',
              'text-xl',
              'font-semibold',
            ]"
          >
            <div class="flex-none text-left">
              <span class="text-xs uppercase font-semibold text-gray-400"> CTR</span>
              <p>{{ pageviewsPerImpression }}%</p>
            </div>

            <div class="flex-none text-left ml-6">
              <span class="text-xs uppercase font-semibold text-gray-400"> CR</span>
              <p>{{ conversionRate }}%</p>
            </div>

            <div
              v-if="facebookCampaignTotals.offsiteConversions !== null"
              class="flex-none text-left ml-6"
            >
              <span class="text-xs uppercase font-semibold text-gray-400"> leads</span>
              <p>{{ campaign.totalContactRequests }}</p>
            </div>

            <div v-if="spendPerLead !== '-'" class="flex-none text-right ml-6">
              <span class="text-xs uppercase font-semibold text-gray-400"> cost</span>
              <p>{{ spendPerLead }} <span class="text-xs">&euro;</span></p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="campaign.totalChannels" class="pt-4 -mx-4">
        <table class="table w-full border-collapse">
          <thead>
            <tr class="bg-gray-100 text-sm text-gray-600 uppercase">
              <th class="py-2 px-3 border border-l-0 text-left w-px" />
              <th class="py-2 px-3 border text-left" colspan="2">channel</th>
              <th class="py-2 px-3 border text-right">impressions</th>
              <th class="py-2 px-3 border text-right">reach</th>
              <th class="py-2 px-3 border text-right">frequency</th>
              <th class="py-2 px-3 border text-right">pageviews</th>
              <th class="py-2 px-3 border text-right">contact requests</th>
              <th class="py-2 px-3 border border-r-0 text-right">spend</th>
            </tr>
          </thead>
          <FacebookTableRow
            v-if="facebookCampaigns && facebookCampaigns.length"
            :facebook-campaigns="facebookCampaigns"
            :facebook-campaign-totals="facebookCampaignTotals"
          />
          <GoogleAdsTableRow
            v-if="campaign.gaCampaigns && campaign.gaCampaigns.length"
            :ga-campaigns="campaign.gaCampaigns"
            :ga-campaign-reports="campaign.gaReportsAggregatedOnCampaignLevel"
          />
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { flatMapDepth } from 'lodash-es';
import { mapState } from 'vuex';
import VDropdown from '@/components/Common/VDropdown.vue';
import VDropdownMenu from '@/components/Common/VDropdownMenu.vue';
import mediableMixin from '@/mixins/mediableMixin';
import FacebookTableRow from '@/components/Campaigns/FacebookTableRow.vue';
import GoogleAdsTableRow from '@/components/Campaigns/GoogleAdsTableRow.vue';

export default {
  mixins: [mediableMixin],
  components: {
    FacebookTableRow,
    GoogleAdsTableRow,
    VDropdown,
    VDropdownMenu,
  },
  props: {
    campaign: { type: Object, required: true },
  },
  computed: {
    ...mapState('entities', {
      facebookCampaignById: (state) => state.facebookCampaigns.byId,
      facebookAdById: (state) => state.facebookAds.byId,
      channelById: (state) => state.channels.byId,
      project({ projects }) {
        if (!this.campaign.projectUuid) return null;

        return projects.byId[this.campaign.projectUuid] || null;
      },
    }),
    cover() {
      if (!this.campaign || !this.campaign.media || !Array.isArray(this.campaign.media)) {
        return null;
      }

      const cover = this.campaign.media.find((medium) => medium.collection === 'covers');

      return cover || null;
    },

    budgetPerResult() {
      if (!this.campaign) return null;
      if (!this.campaign.budget) return null;
      if (!this.campaign.result) return null;
      if (this.campaign.isBudgetPerResult) return this.campaign.budget;
      return (this.campaign.budget / this.campaign.result).toFixed(2);
    },

    channels() {
      if (!this.campaign.channels) return [];
      return this.campaign.channels.map((channelId) => this.channelById[channelId]);
    },

    isActive() {
      return this.channels.some((channel) => {
        if (!channel.facebookCampaign) return false;
        const channable = this.facebookCampaignById[channel.facebookCampaign];
        if (!channable) return false;
        return channable.status === 'ACTIVE';
      });
    },
    facebookCampaigns() {
      return this.channels
        .filter((channel) => channel.channableType === 'facebook_campaign')
        .map((channel) => channel.channableData)
        .filter(Boolean);
    },
    facebookCampaignTotals() {
      return flatMapDepth(this.facebookCampaigns, 'facebook_campaign_insights', 1).reduce(
        (acc, current) => ({
          impressions: acc.impressions + (current?.impressions || 0),
          reach: acc.reach + (current?.reach || 0),
          clicks: acc.clicks + (current?.clicks || 0),
          inlineLinkClicks: acc.inlineLinkClicks + (current?.inline_link_clicks || 0),
          offsiteConversions: acc.offsiteConversions + (current?.offsiteConversions || 0),
          spend: acc.spend + (current?.spend || 0) / 100,
        }),
        {
          impressions: 0,
          reach: 0,
          clicks: 0,
          inlineLinkClicks: 0,
          offsiteConversions: 0,
          spend: 0,
        }
      );
    },
    gaCampaignTotals() {
      return (this.campaign?.gaReportsAggregatedOnCampaignLevel || [])
        .filter((report) => report.ga_ad_group_id === 0 && report.ga_ad_id === 0)
        .reduce(
          (acc, current) => ({
            impressions: acc.impressions + (current?.impressions || 0),
            reach: acc.reach + (current?.reach || 0),
            clicks: acc.clicks + (current?.clicks || 0),
            inlineLinkClicks: acc.clicks + (current?.clicks || 0),
            spend: acc.spend + (current?.cost_micros || 0) / 1_000_000,
          }),
          {
            impressions: 0,
            reach: 0,
            clicks: 0,
            inlineLinkClicks: 0,
            offsiteConversions: 0,
            spend: 0,
          }
        );
    },
    campaignTotals() {
      return [this.facebookCampaignTotals, this.gaCampaignTotals].reduce(
        (acc, current) => ({
          impressions: acc.impressions + current?.impressions,
          reach: acc.reach + current.reach,
          clicks: acc.clicks + current?.clicks,
          inlineLinkClicks: acc.inlineLinkClicks + current.inlineLinkClicks,
          spend: acc.spend + current?.spend,
        }),
        {
          impressions: 0,
          reach: 0,
          clicks: 0,
          inlineLinkClicks: 0,
          offsiteConversions: 0,
          spend: 0,
        }
      );
    },
    pageviewsPerImpression() {
      const { impressions, inlineLinkClicks } = this.campaignTotals;
      if (!impressions) return 0;
      return +((inlineLinkClicks / impressions) * 100).toFixed(2);
    },
    conversionRate() {
      const { inlineLinkClicks } = this.campaignTotals;
      const { totalContactRequests: contactRequests = 0 } = this.campaign;

      if (!Number.isFinite(contactRequests) || !Number.isFinite(inlineLinkClicks)) return '-';

      if (inlineLinkClicks > 0) {
        return (100 * (contactRequests / inlineLinkClicks)).toFixed(2);
      }
      return '-';
    },
    spendPerLead() {
      const { spend = 0 } = this.campaignTotals || {};
      const { totalContactRequests: contactRequests = 0 } = this.campaign;
      if (Number.isFinite(spend) && Number.isFinite(contactRequests) && contactRequests > 0) {
        return (spend / contactRequests).toFixed(2);
      }
      return '-';
    },
  },
};
</script>
